import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { NumberLineItem, LayoutItem, WidgetJSON } from '../../../../transformers/zod-schemas';
import type { WidgetValidation } from '../../../../validators';
import { LayoutRenderer } from '../../LayoutRenderer';
import type { UserAnswerValue } from '../../../types';
import { FreeDragAxis } from './FreeDragAxis';
import { AxisTicks } from './AxisTicks';
import type { CursorValidationStatuses } from './CursorBalls';
import { AxisLabels } from './AxisLabels';
import { HighlightLabelsRow } from './HighlightLabelsRow';
import { CounterControls } from './CounterControls';
import { ArrowArcsRow } from './ArrowArcsRow';
import { useMeasuredPositionContentHeight } from './useMeasuredPositionContentHeight';
import {
  toPercent,
  getMajorTicks,
  getMinorTickValues,
  getPositionWidgetId,
  cleanFloat,
  roundTick,
  SIDE_PAD,
  CSS_VAR_SIDE_PAD,
} from '../../../utils/number-line.utils';
import { DRAG_OPTIONS_WIDGET_ID } from '../../../../constants';

export interface NumberLineRendererProps {
  item: NumberLineItem;
  widgets: Record<string, WidgetJSON>;
  values: Record<string, UserAnswerValue>;
  droppedInputs: Set<string>;
  validationDetails?: Record<string, WidgetValidation> | null;
  onChange: (widgetId: string, value: UserAnswerValue, fromDrop?: boolean, sourceOptionId?: string) => void;
  onInputFocus?: (widgetId: string) => void;
}

export const NumberLineRenderer: React.FC<NumberLineRendererProps> = ({
  item,
  widgets,
  values,
  droppedInputs,
  validationDetails,
  onChange,
  onInputFocus,
}) => {
  const {
    min, max, step,
    minorTicks = 0,
    style: lineStyle = 'arrow-right',
    labelVisibility,
    highlights,
    arrows,
    counter,
    positions,
    cursors,
  } = item;

  const [counterCount, setCounterCount] = useState<number>(
    counter !== undefined ? (counter.default ?? counter.min) : 0,
  );

  const defaultCounterCount = counter !== undefined ? (counter.default ?? counter.min) : 0;

  // Sync when exercise definition changes without unmounting.
  useEffect(() => {
    setCounterCount(defaultCounterCount);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counter]);

  // NumberLine is a layout, not a widget. However, counter and cursors intentionally
  // participate in the shared validation flow as validated internal inputs:
  // their current values are reported via onChange so the simple validator can
  // compare them against answers[id] without any validator-core changes.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    if (counter !== undefined) {
      onChangeRef.current(counter.id, String(counterCount));
    }
  }, [counterCount, counter?.id]);

  // Seed cursor initial positions when the cursor definition changes (new id or new initial value).
  // Using a stable primitive key mirrors the counter pattern `[counterCount, counter?.id]` and
  // prevents accidental resets on parent re-renders where `cursors` gets a new array reference.
  const cursorKeys = useMemo(
    () => (cursors ?? []).map(c => `${c.id}:${c.value}`).join(','),
    [cursors],
  );
  useEffect(() => {
    for (const cursor of cursors ?? []) {
      onChangeRef.current(cursor.id, String(cursor.value));
    }
  }, [cursorKeys]);

  // Counter overrides step when active: effectiveStep = (max - min) / counterCount.
  const effectiveStep = useMemo(() => {
    if (counter !== undefined && counterCount > 0) {
      return cleanFloat((max - min) / counterCount);
    }
    return step;
  }, [counter, counterCount, min, max, step]);

  // Rounded to DISPLAY_PRECISION and deduped: guarantees unique React keys for labels.
  // Downstream consumers (getMinorTickValues, FreeDragAxis, snap logic) intentionally
  // receive display-precision values so snap targets match what the user sees.
  const majorTicks = useMemo(
    () => [...new Set(getMajorTicks(min, max, effectiveStep).map(roundTick))],
    [min, max, effectiveStep],
  );
  const minorValues = useMemo(
    () => getMinorTickValues(majorTicks, minorTicks),
    [majorTicks, minorTicks],
  );

  const showArrowRight = lineStyle === 'arrow-right' || lineStyle === 'arrow-both' ;
  const showArrowLeft  = lineStyle === 'arrow-left'  || lineStyle === 'arrow-both';

  const hasDragOptions = Boolean(widgets[DRAG_OPTIONS_WIDGET_ID]);
  const isAxisDragMode = hasDragOptions && item.dragMode === 'axis';

  // Pre-compute occupied ticks to hide their labels without an O(n²) scan.
  const inputTickSet = useMemo(() => {
    if (isAxisDragMode) return new Set<number>();
    return new Set(
      positions
        .filter(pos => getPositionWidgetId(pos.content) !== null)
        .map(pos => roundTick(pos.value)),
    );
  }, [isAxisDragMode, positions]);

  // In axis drag mode, input positions are rendered by FreeDragAxis — skip them here.
  const visiblePositions = useMemo(
    () => positions.filter(pos => !isAxisDragMode || getPositionWidgetId(pos.content) === null),
    [isAxisDragMode, positions],
  );

  const visiblePositionIds = useMemo(
    () => visiblePositions.map(pos => pos.id),
    [visiblePositions],
  );
  const { positionContentHeight, getPositionContentRef } =
    useMeasuredPositionContentHeight(visiblePositionIds);

  // Axis colour reflects the counter division only — position inputs have their own per-widget feedback.
  // Skipped when counter.answer is absent: the validator marks it "correct" by default which would
  // wrongly colour the axis green.
  const nlValidationStatus = useMemo((): 'correct' | 'incorrect' | null => {
    if (!validationDetails || counter === undefined) return null;
    if (counter.answer === undefined) return null;
    const counterValidation = validationDetails[counter.id];
    if (counterValidation === undefined) return null;
    return counterValidation.isCorrect ? 'correct' : 'incorrect';
  }, [validationDetails, counter]);

  const cursorValidationStatuses = useMemo((): CursorValidationStatuses | undefined => {
    if (!validationDetails || !cursors?.length) return undefined;

    const statuses: CursorValidationStatuses = {};
    for (const cursor of cursors) {
      const detail = validationDetails[cursor.id];
      if (detail !== undefined) {
        statuses[cursor.id] = detail.isCorrect ? 'correct' : 'incorrect';
      }
    }
    return statuses;
  }, [validationDetails, cursors]);

  return (
    <div
      className={`number-line${isAxisDragMode ? ' number-line--drag-mode' : ''}${lineStyle === 'line' ? ' number-line--style-line' : ''}${nlValidationStatus ? ` number-line--${nlValidationStatus}` : ''}`}
      style={{
        [CSS_VAR_SIDE_PAD]: `${SIDE_PAD}%`,
        ...(positionContentHeight && { '--nl-position-content-height': positionContentHeight }),
      } as React.CSSProperties}
      role="group"
      aria-label={`Number line from ${min} to ${max}`}
    >
      {/* Row 1 — position content (labels / inputs above the axis) */}
      {visiblePositions.length > 0 && (
        <div className="number-line-content-row">
          {visiblePositions.map(pos => (
            <div
              key={pos.id}
              ref={getPositionContentRef(pos.id)}
              className="number-line-position-content"
              style={{ left: `${toPercent(pos.value, min, max)}%` }}
            >
              {pos.content.map((subItem: LayoutItem) => (
                <LayoutRenderer
                  key={subItem.id}
                  item={subItem}
                  widgets={widgets}
                  values={values}
                  droppedInputs={droppedInputs}
                  validationDetails={validationDetails}
                  onChange={onChange}
                  onInputFocus={onInputFocus}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Row 2 — arrow annotations */}
      <ArrowArcsRow
        arrows={arrows}
        min={min}
        max={max}
        widgets={widgets}
        values={values}
        droppedInputs={droppedInputs}
        validationDetails={validationDetails}
        onChange={onChange}
        onInputFocus={onInputFocus}
      />

      {/* Row 3 — axis */}
      {isAxisDragMode ? (
        <FreeDragAxis
          axisDropId={`${item.id}--nl-axis`}
          min={min} max={max}
          minorTicks={minorTicks}
          majorTicks={majorTicks}
          showArrowLeft={showArrowLeft}
          showArrowRight={showArrowRight}
          positions={positions}
          values={values}
          onChange={onChange}
          highlights={highlights}
          cursors={cursors}
          cursorValidationStatuses={cursorValidationStatuses}
          snapKey={counterCount}
        />
      ) : (
        <AxisTicks
          min={min} max={max}
          majorTicks={majorTicks}
          minorValues={minorValues}
          showArrowLeft={showArrowLeft}
          showArrowRight={showArrowRight}
          highlights={highlights}
          positions={positions}
          cursors={cursors}
          onChange={onChange}
          values={values}
          cursorValidationStatuses={cursorValidationStatuses}
        />
      )}

      {/* Row 4 — tick labels */}
      <AxisLabels
        majorTicks={majorTicks}
        min={min} max={max}
        labelVisibility={labelVisibility}
        inputTickSet={inputTickSet}
      />

      {/* Row 5 — highlight labels */}
      <HighlightLabelsRow highlights={highlights} min={min} max={max} />

      {/* Row 6 — counter controls */}
      {counter !== undefined && (
        <CounterControls
          counter={counter}
          count={counterCount}
          onDecrement={() => setCounterCount(c => Math.max(counter.min, c - 1))}
          onIncrement={() => setCounterCount(c => Math.min(counter.max, c + 1))}
        />
      )}
    </div>
  );
};
